import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import Breadcrumbs from '../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 6rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(37, 99, 235, 0.05) 0%, transparent 50%);
    opacity: 0.8;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroBadge = styled(motion.span)`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background: white;
  color: #1e40af;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(37, 99, 235, 0.2);
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 4rem;
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.1),
              0 10px 20px rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(37, 99, 235, 0.2),
                0 12px 24px rgba(37, 99, 235, 0.2);
  }
`;

const SecondaryButton = styled(motion.button)`
  background: white;
  color: #2563eb;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  border: 2px solid rgba(37, 99, 235, 0.2);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(37, 99, 235, 0.05);
    border-color: #2563eb;
    transform: translateY(-2px);
  }
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  
  span {
    display: block;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 3rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(37, 99, 235, 0.1);
  }
`;

const ServiceIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: #64748b;
  margin: 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;

  &:before {
    content: '✓';
    color: #2563eb;
    font-weight: bold;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  padding: 6rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

const CTAButton = styled(motion.button)`
  background: white;
  color: #2563eb;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

function Services() {
  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Services', path: '/services' }]} />
      <Hero>
        <HeroContent>
          <HeroBadge
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            Enterprise-Grade Testing Solutions
          </HeroBadge>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span>Comprehensive Testing</span>
            <span>Solutions for Shopify Stores</span>
          </Title>
          <Subtitle>
            Protect your revenue and ensure a flawless shopping experience with our suite of testing services.
            Our advanced platform helps you catch issues before they impact your customers.
          </Subtitle>
          <HeroButtons>
            <ScrollLink to="auditForm" smooth={true} duration={800} offset={-100}>
              <PrimaryButton
                as="div"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  window.location.href = '/free-trial';
                }}
              >
                Get Started Free
              </PrimaryButton>
            </ScrollLink>
            <SecondaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/pricing'}
            >
              View Pricing
            </SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </Hero>

      <ServicesGrid>
        {[
          {
            icon: '🛒',
            title: 'Checkout Testing',
            description: 'Ensure your checkout process works flawlessly across all scenarios.',
            features: [
              'Payment gateway integration testing',
              'Cart functionality verification',
              'Discount code validation',
              'Cross-browser compatibility',
              'Mobile responsiveness checks'
            ]
          },
          {
            icon: '⚡',
            title: 'Performance Testing',
            description: 'Optimize your store\'s speed and handle high traffic with confidence.',
            features: [
              'Load time optimization',
              'Server response testing',
              'Database performance checks',
              'CDN configuration testing',
              'Cache effectiveness analysis'
            ]
          },
          {
            icon: '🔄',
            title: 'Integration Testing',
            description: 'Verify all your third-party apps and services work together seamlessly.',
            features: [
              'API endpoint testing',
              'Third-party app compatibility',
              'Data synchronization checks',
              'Error handling verification',
              'Security compliance testing'
            ]
          },
          {
            icon: '🔒',
            title: 'Security Testing',
            description: 'Protect your store and customer data with comprehensive security testing.',
            features: [
              'Vulnerability assessments',
              'PCI compliance checks',
              'Data encryption verification',
              'Access control testing',
              'Security patch validation'
            ]
          },
          {
            icon: '📱',
            title: 'Mobile Testing',
            description: 'Ensure perfect functionality across all mobile devices and platforms.',
            features: [
              'Responsive design testing',
              'Mobile payment testing',
              'Touch interaction testing',
              'App-like experience validation',
              'Cross-device compatibility'
            ]
          },
          {
            icon: '🤖',
            title: 'Automation Testing',
            description: 'Streamline your testing process with intelligent automation.',
            features: [
              'Automated test script creation',
              'Continuous integration testing',
              'Regression testing',
              'Test scenario management',
              'Automated reporting'
            ]
          }
        ].map((service, index) => (
          <ServiceCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
            onClick={() => {
              const paths = {
                'Checkout Testing': '/services/checkout',
                'Performance Testing': '/services/performance',
                'Integration Testing': '/services/integration',
                'Security Testing': '/services/security',
                'Mobile Testing': '/services/mobile',
                'Automation Testing': '/services/automation'
              };
              window.location.href = paths[service.title];
            }}
            style={{ cursor: 'pointer' }}
          >
            <ServiceIcon>{service.icon}</ServiceIcon>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
            <FeatureList>
              {service.features.map((feature, i) => (
                <FeatureItem key={i}>{feature}</FeatureItem>
              ))}
            </FeatureList>
          </ServiceCard>
        ))}
      </ServicesGrid>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Protect Your Revenue?</CTATitle>
          <CTAText>
            Get a comprehensive audit of your Shopify store and discover potential issues before they impact your customers.
          </CTAText>
          <ScrollLink to="auditForm" smooth={true} duration={800} offset={-100}>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.location.href = '/free-trial';
              }}
            >
              Get Your Free Audit
            </CTAButton>
          </ScrollLink>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default Services;